<template>
  <v-navigation-drawer
    :model-value="isMobile ? open : true"
    :rail="!showExpanded"
    :rail-width="72"
    :width="isMobile ? 268 : 240"
    :permanent="!isMobile"
    :temporary="isMobile"
    class="app-sidebar"
    :class="{ 'app-sidebar--expanded': showExpanded, 'app-sidebar--mobile': isMobile }"
    @update:model-value="onDrawerToggle">
    <!-- Logo -->
    <div class="app-sidebar__logo" @click="goHome">
      <img
        v-if="showExpanded"
        src="@/assets/images/header-logo-white.png"
        alt="Lamar Health"
        class="app-sidebar__logo-full" />
      <img v-else src="@/assets/images/header-logo-white-icon.png" alt="LH" class="app-sidebar__logo-icon" />
    </div>

    <CompanySwitcher v-if="!isDemo" :expanded="showExpanded" />

    <!-- Nav items -->
    <div class="app-sidebar__nav">
      <SidebarNavItem
        v-if="!isDemo"
        icon="mdi-inbox-outline"
        label="Inbox"
        route="inbox"
        :count="inboxStore.unreadCount"
        :expanded="showExpanded"
        @click="closeOnMobile" />
      <SidebarNavItem
        icon="mdi-tray-arrow-down"
        label="Intake"
        :route="r('intake')"
        :expanded="showExpanded"
        @click="closeOnMobile" />
      <SidebarNavItem
        icon="mdi-file-document-check-outline"
        label="Prior Auth"
        :route="r('prior-auth')"
        :expanded="showExpanded"
        @click="closeOnMobile" />
      <SidebarNavItem
        v-if="cmmEnabled"
        icon="mdi-import"
        label="Import CMM"
        :route="r('import-cmm-submission')"
        :expanded="showExpanded"
        @click="closeOnMobile" />
      <SidebarNavItem
        v-if="voiceAiEnabled"
        icon="mdi-phone-in-talk"
        label="Voice AI"
        route="voice-ai"
        :expanded="showExpanded"
        @click="closeOnMobile" />
    </div>

    <!-- Footer -->
    <template #append>
      <div class="app-sidebar__footer">
        <SidebarNavItem
          icon="mdi-flag-outline"
          label="Report an Issue"
          :expanded="showExpanded"
          @click="onReportIssue" />
        <SidebarNavItem
          v-if="!isDemo"
          icon="mdi-file-sign"
          label="Delegate Agreements"
          :route="r('delegate-agreements')"
          :expanded="showExpanded"
          @click="closeOnMobile" />
        <SidebarNavItem
          v-if="authStore.userInfo.isAdmin && !isDemo"
          icon="mdi-account-multiple-outline"
          label="User Management"
          :route="r('user-management')"
          :expanded="showExpanded"
          @click="closeOnMobile" />
        <SidebarNavItem icon="mdi-cog-outline" label="Settings" :expanded="showExpanded" @click="onOpenSettings" />
        <SidebarNavItem icon="mdi-logout" label="Log Out" :expanded="showExpanded" @click="logout" />
        <button v-if="!isMobile" class="app-sidebar__toggle" @click="toggleExpanded">
          <v-icon :icon="isExpanded ? 'mdi-chevron-left' : 'mdi-chevron-right'" size="18" />
        </button>
      </div>
    </template>
  </v-navigation-drawer>

  <ReportIssueDialog v-model="showReportIssueDialog" />

  <v-snackbar
    v-model="showMentionToast"
    location="bottom right"
    timeout="8000"
    class="mention-toast"
    content-class="mention-toast__content">
    <div class="mention-toast__body">
      <v-icon icon="mdi-inbox-outline" size="18" class="mention-toast__icon" />
      <span v-if="toastMention" class="mention-toast__text">
        <strong>{{ toastSenderName }}</strong>
        {{ toastMention.kind === "assignment" ? "assigned you to" : "mentioned you on" }}
        <span class="mention-toast__key">{{ toastKey }}</span>
      </span>
      <span v-else class="mention-toast__text">
        <strong>{{ toastDelta }} new {{ toastDelta === 1 ? "mention" : "mentions" }}</strong> in your inbox
      </span>
    </div>
    <template #actions>
      <v-btn variant="text" size="small" class="mention-toast__view" @click="openInboxFromToast">View</v-btn>
    </template>
  </v-snackbar>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAuthStore, useRequirementsStore, useTableSettingsStore, useInboxStore } from "@/stores";
import SidebarNavItem from "@/components/SidebarNavItem.vue";
import CompanySwitcher from "@/components/CompanySwitcher.vue";
import ReportIssueDialog from "@/components/dashboard/ReportIssueDialog.vue";
import { SCOPE } from "@/constants/scope";
import { usePolling } from "@/composables/usePolling";

const props = defineProps({
  isMobile: { type: Boolean, default: false },
  open: { type: Boolean, default: false },
});
const emit = defineEmits(["update:open"]);

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const requirementsStore = useRequirementsStore();
const tableSettingsStore = useTableSettingsStore();
const inboxStore = useInboxStore();

const STORAGE_KEY = "lh_sidebar_expanded";
const isExpanded = ref(localStorage.getItem(STORAGE_KEY) === "true");
const showReportIssueDialog = ref(false);

// On mobile the drawer is a full overlay, so labels are always shown.
const showExpanded = computed(() => props.isMobile || isExpanded.value);

function onDrawerToggle(val) {
  if (props.isMobile) emit("update:open", val);
}

function closeOnMobile() {
  if (props.isMobile) emit("update:open", false);
}

function onReportIssue() {
  showReportIssueDialog.value = true;
  closeOnMobile();
}

function onOpenSettings() {
  openSettings();
  closeOnMobile();
}

const isDemo = computed(() => route.path.startsWith("/demo"));
const r = (name) => (isDemo.value ? `demo-${name}` : name);

const cmmEnabled = computed(() => {
  return requirementsStore.clientConfig?.priorAuth?.cmmIntakeEnabled ?? false;
});

const voiceAiEnabled = ref(false);

usePolling(
  () => {
    if (!isDemo.value) inboxStore.fetchUnreadCount();
  },
  { interval: 60000 }
);

const showMentionToast = ref(false);
const toastMention = ref(null);
const toastDelta = ref(0);

const toastSenderName = computed(() => {
  const sender = toastMention.value?.sender;
  if (!sender) return "A teammate";
  return `${sender.firstName || ""} ${sender.lastName || ""}`.trim() || "A teammate";
});

const toastKey = computed(() => {
  const target = toastMention.value?.target;
  if (!target) return "";
  const key = String(target.key);
  if (target.kind === "intake_order") return `#${key}`;
  return key.length > 12 ? `${key.slice(0, 10)}…` : key;
});

watch(
  () => inboxStore.arrival,
  (arrival) => {
    if (!arrival) return;
    inboxStore.clearArrival();
    if (isDemo.value || route.name === "inbox") return;
    toastMention.value = arrival.delta === 1 ? arrival.mention : null;
    toastDelta.value = arrival.delta;
    showMentionToast.value = true;
  }
);

function openInboxFromToast() {
  showMentionToast.value = false;
  router.push({ name: "inbox" });
}

onMounted(async () => {
  if (!isDemo.value) inboxStore.fetchUnreadCount();
  if (!requirementsStore.clientConfig) {
    await requirementsStore.refreshCurrentUserClientConfig();
  }
  // Voice AI gating is independent of the legacy clientConfig broadcast (which we don't
  // own). Hit the dedicated config endpoint once; tenants without VoiceAIConfig get isEnabled=false.
  try {
    const { voiceAiService } = await import("@/services/voiceAiService");
    const cfg = await voiceAiService.getConfig();
    voiceAiEnabled.value = Boolean(cfg?.isEnabled);
  } catch {
    voiceAiEnabled.value = false;
  }
});

function toggleExpanded() {
  isExpanded.value = !isExpanded.value;
  localStorage.setItem(STORAGE_KEY, String(isExpanded.value));
}

function goHome() {
  const page = requirementsStore.clientConfig?.defaultPostLoginPage || "prior-auth";
  router.push({ name: r(page) });
}

// Map route name → settings scope so opening Settings from a given table
// preselects the matching tab. Legacy route names (all-patients / all-submissions)
// are kept alongside the new dashboard names.
const ROUTE_SCOPE = Object.freeze({
  intake: SCOPE.INTAKE,
  "all-patients": SCOPE.INTAKE,
  "prior-auth": SCOPE.PA,
  "all-submissions": SCOPE.PA,
});

function openSettings() {
  tableSettingsStore.open(ROUTE_SCOPE[route.name]);
}

async function logout() {
  await authStore.logout();
  window.location.reload();
}
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.app-sidebar {
  background: $lh_sidebar_bg !important;
  border-right: none !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.app-sidebar__logo {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 12px;
  cursor: pointer;
  min-height: 64px;
}

.app-sidebar__logo-full {
  height: 28px;
  width: auto;
  object-fit: contain;
}

.app-sidebar__logo-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.app-sidebar__nav {
  flex: 1;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.app-sidebar__footer {
  padding: 8px 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  border-top: 1px solid rgba(white, 0.08);
}

.app-sidebar__toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 8px 0;
  margin-top: 4px;
  border: none;
  background: transparent;
  color: $lh_sidebar_text;
  cursor: pointer;
  border-radius: 8px;
  transition: color $lh_transition_fast, background-color $lh_transition_fast;

  &:hover {
    color: $lh_sidebar_text_active;
    background: $lh_sidebar_bg_hover;
  }
}
</style>

<style lang="scss">
@import "@/styles/_variables.scss";

.mention-toast .v-snackbar__wrapper {
  background: $lh_sidebar_bg !important;
  border-radius: 12px !important;
  box-shadow: $lh_shadow_lg !important;
}

.mention-toast__body {
  display: flex;
  align-items: center;
  gap: 10px;
}

.mention-toast__icon {
  color: $lh_sidebar_accent;
  flex-shrink: 0;
}

.mention-toast__text {
  font-family: $lh_font_family_dm_sans;
  font-size: 13.5px;
  color: #ffffff;

  strong {
    font-weight: 700;
  }
}

.mention-toast__key {
  font-family: $lh_font_mono;
  font-size: 12.5px;
  background: rgba(#ffffff, 0.12);
  border-radius: 4px;
  padding: 1px 6px;
}

.mention-toast__view {
  color: $lh_sidebar_accent !important;
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0;
}
</style>
